/**
 * Dados do pagamento eletrônico da venda (PIX, crédito, débito e outros) que a nota leva em
 * detPag: instituição/credenciadora, bandeira, autorização/NSU, TxId/EndToEndId. Só DADOS —
 * nenhuma baixa financeira acontece aqui. O banco confere a soma e trava depois que a venda
 * tem documento fiscal ativo.
 */
import {
  CARD_BRANDS,
  formatMoney,
  toFormMoney,
  type PaymentInstitution,
  type SalePaymentDetailInput,
  type SalePaymentDetailView,
  type SalesOrder,
} from "@cafegestor/shared";
import { useState } from "react";
import { useCan } from "@/app/me";
import { Alert, Button, Field, Input, Select, Table } from "@/components/ui";
import { toDecimal } from "@/lib/decimal";
import { DecimalInput } from "@/components/DecimalInput";
import { SaveError } from "../cadastros/widgets";
import { usePaymentMethods } from "../compras/lib";
import { TPAG_LABEL, useFiscalAction, useFiscalQuery } from "./lib";

type Row = {
  paymentMethodId: string;
  amount: string;
  integrationType: "" | "1" | "2";
  institutionId: string;
  cardBrand: string;
  authorizationCode: string;
  nsu: string;
  pixTxid: string;
  pixEndToEnd: string;
  terminalId: string;
  /** guardados como vieram (ex.: hora do PIX registrada na rota); a tela não os edita */
  paidAt: string | null;
  notes: string | null;
};

const emptyRow = (methodId: string, amount: string): Row => ({
  paymentMethodId: methodId,
  amount,
  integrationType: "",
  institutionId: "",
  cardBrand: "",
  authorizationCode: "",
  nsu: "",
  pixTxid: "",
  pixEndToEnd: "",
  terminalId: "",
  paidAt: null,
  notes: null,
});

const fromView = (d: SalePaymentDetailView): Row => ({
  paymentMethodId: d.paymentMethodId,
  amount: toFormMoney(d.amount),
  integrationType: d.integrationType === "1" || d.integrationType === "2" ? d.integrationType : "",
  institutionId: d.institutionId ?? "",
  cardBrand: d.cardBrand ?? "",
  authorizationCode: d.authorizationCode ?? "",
  nsu: d.nsu ?? "",
  pixTxid: d.pixTxid ?? "",
  pixEndToEnd: d.pixEndToEnd ?? "",
  terminalId: d.terminalId ?? "",
  paidAt: d.paidAt,
  notes: d.notes,
});

const orNull = (v: string) => (v.trim() === "" ? null : v.trim());

/** Só os campos do tipo de pagamento escolhido vão para a nota (os escondidos são limpos). */
function toInput(r: Row, pix: boolean): SalePaymentDetailInput {
  return {
    paymentMethodId: r.paymentMethodId,
    amount: toDecimal(r.amount, "dinheiro"),
    integrationType: r.integrationType === "" ? null : r.integrationType,
    institutionId: orNull(r.institutionId),
    cardBrand: pix ? null : orNull(r.cardBrand),
    authorizationCode: pix ? null : orNull(r.authorizationCode),
    nsu: pix ? null : orNull(r.nsu),
    pixTxid: pix ? orNull(r.pixTxid) : null,
    pixEndToEnd: pix ? orNull(r.pixEndToEnd) : null,
    terminalId: orNull(r.terminalId),
    paidAt: r.paidAt,
    notes: r.notes,
  };
}

export function SalePaymentsEditor({ order }: { order: SalesOrder }) {
  const can = useCan();
  const saved = useFiscalQuery<SalePaymentDetailView[]>(`/vendas/${order.id}/pagamentos`);
  if (!saved.data) return null;
  // chave pelos dados gravados: depois de salvar, o formulário recomeça do que ficou no banco
  return (
    <PaymentsForm
      key={JSON.stringify(saved.data)}
      order={order}
      saved={saved.data}
      canEdit={
        can("vendas.pedido.criar") ||
        can("vendas.rota.confirmar") ||
        can("financeiro.baixa.registrar")
      }
      canSeeInstitutions={can("fiscal.ver")}
    />
  );
}

function PaymentsForm({
  order,
  saved,
  canEdit,
  canSeeInstitutions,
}: {
  order: SalesOrder;
  saved: SalePaymentDetailView[];
  canEdit: boolean;
  canSeeInstitutions: boolean;
}) {
  const methods = usePaymentMethods();
  const institutions = useFiscalQuery<PaymentInstitution[]>(
    "/pagamentos/instituicoes",
    canSeeInstitutions,
  );
  const [rows, setRows] = useState<Row[]>(saved.map(fromView));
  const save = useFiscalAction<{ details: SalePaymentDetailInput[] }>(
    () => `/vendas/${order.id}/pagamentos`,
    "PUT",
  );
  const setRow = (i: number, patch: Partial<Row>) =>
    setRows(rows.map((r, n) => (n === i ? { ...r, ...patch } : r)));
  const methodOf = (id: string) => methods.data?.items.find((m) => m.id === id);
  const total = rows.reduce((s, r) => s + (Number(toDecimal(r.amount, "dinheiro")) || 0), 0);

  return (
    <div className="space-y-3 rounded-md border p-3">
      <h3 className="font-semibold">Pagamento eletrônico (dados para a nota)</h3>
      <p className="text-muted-foreground">
        Informe aqui PIX, cartão e outros pagamentos eletrônicos desta venda. A parte não detalhada
        vai na nota pela forma de pagamento do pedido. Nenhum recebimento é baixado aqui. Total da
        venda: {formatMoney(order.totalAmount)}.
      </p>
      {rows.length === 0 && <p className="text-muted-foreground">Nenhum detalhe informado.</p>}
      {rows.length > 0 && (
        <Table head={["Forma", "Valor", "Instituição / bandeira", "Identificação", ""]}>
          {rows.map((r, i) => {
            const m = methodOf(r.paymentMethodId);
            const pix = m?.nfePaymentCode === "17";
            return (
              <tr key={i} className="align-top">
                <td className="px-3 py-2">
                  <Select
                    disabled={!canEdit}
                    value={r.paymentMethodId}
                    onChange={(e) => setRow(i, { paymentMethodId: e.target.value })}
                  >
                    {methods.data?.items.map((pm) => (
                      <option key={pm.id} value={pm.id}>
                        {pm.name} ({TPAG_LABEL[pm.nfePaymentCode] ?? pm.nfePaymentCode})
                      </option>
                    ))}
                  </Select>
                </td>
                <td className="text-right tabular-nums px-3 py-2">
                  <DecimalInput
                    className="w-28"
                    disabled={!canEdit}
                    value={r.amount}
                    onChange={(e) => setRow(i, { amount: e.target.value })}
                  />
                </td>
                <td className="space-y-1 px-3 py-2">
                  <Select
                    disabled={!canEdit}
                    value={r.integrationType}
                    onChange={(e) =>
                      setRow(i, { integrationType: e.target.value as Row["integrationType"] })
                    }
                  >
                    <option value="">Integração: não informada</option>
                    <option value="1">Integrado (TEF/PIX automático)</option>
                    <option value="2">Não integrado (POS/maquininha)</option>
                  </Select>
                  {canSeeInstitutions ? (
                    <Select
                      disabled={!canEdit}
                      value={r.institutionId}
                      onChange={(e) => setRow(i, { institutionId: e.target.value })}
                    >
                      <option value="">Instituição: padrão da forma</option>
                      {institutions.data?.map((inst) => (
                        <option key={inst.id} value={inst.id}>
                          {inst.name}
                        </option>
                      ))}
                    </Select>
                  ) : null}
                  {!pix && (
                    <Select
                      disabled={!canEdit}
                      value={r.cardBrand}
                      onChange={(e) => setRow(i, { cardBrand: e.target.value })}
                    >
                      <option value="">Bandeira: —</option>
                      {Object.entries(CARD_BRANDS).map(([code, label]) => (
                        <option key={code} value={code}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  )}
                </td>
                <td className="space-y-1 px-3 py-2">
                  {pix ? (
                    <>
                      <Field label="TxId">
                        <Input
                          disabled={!canEdit}
                          value={r.pixTxid}
                          onChange={(e) => setRow(i, { pixTxid: e.target.value })}
                        />
                      </Field>
                      <Field label="EndToEndId">
                        <Input
                          disabled={!canEdit}
                          value={r.pixEndToEnd}
                          onChange={(e) => setRow(i, { pixEndToEnd: e.target.value })}
                        />
                      </Field>
                    </>
                  ) : (
                    <>
                      <Field label="Autorização">
                        <Input
                          disabled={!canEdit}
                          value={r.authorizationCode}
                          onChange={(e) => setRow(i, { authorizationCode: e.target.value })}
                        />
                      </Field>
                      <Field label="NSU">
                        <Input
                          disabled={!canEdit}
                          value={r.nsu}
                          onChange={(e) => setRow(i, { nsu: e.target.value })}
                        />
                      </Field>
                    </>
                  )}
                  <Field label="Terminal">
                    <Input
                      disabled={!canEdit}
                      value={r.terminalId}
                      onChange={(e) => setRow(i, { terminalId: e.target.value })}
                    />
                  </Field>
                </td>
                <td className="px-3 py-2 text-right">
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setRows(rows.filter((_, n) => n !== i))}
                    >
                      Remover
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </Table>
      )}
      {total > Number(order.totalAmount) + 0.004 && (
        <Alert tone="warn">A soma dos detalhes passa do total da venda.</Alert>
      )}
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={!methods.data?.items.length}
            onClick={() =>
              setRows([
                ...rows,
                emptyRow(
                  order.paymentMethodId ?? methods.data?.items[0]?.id ?? "",
                  rows.length === 0 ? order.totalAmount : "",
                ),
              ])
            }
          >
            Adicionar pagamento
          </Button>
          <Button
            size="sm"
            disabled={save.isPending}
            onClick={() =>
              save.mutate({
                details: rows.map((r) =>
                  toInput(r, methodOf(r.paymentMethodId)?.nfePaymentCode === "17"),
                ),
              })
            }
          >
            Salvar dados do pagamento
          </Button>
        </div>
      )}
      <SaveError error={save.error} />
    </div>
  );
}
